/**
 * Tickets tab of an event: how the online events page sells or registers places (free
 * registration or paid ticket — the price per person decides —, capacity and minimum party), the
 * figures (tickets issued, revenue, check-ins, bookings) and the entrance tickets issued
 * (EventTicket, one per place, their QR travels in the confirmation email).
 */
class EventDetailTicketsViewController extends MUIViewController
{
    private accessSegmented:MUISegmentedControl = null;
    private priceTextField:MUITextField = null;
    private maxPaxTextField:MUITextField = null;
    private minPaxTextField:MUITextField = null;
    private issuedLabel:MUILabel = null;
    private revenueLabel:MUILabel = null;
    private checkinLabel:MUILabel = null;
    private bookingsLabel:MUILabel = null;
    private tableView:UITableView = null;

    private cf:MIONumberFormatter = MUIWebApplication.sharedInstance().delegate.currencyFormatter;
    private nf:MIONumberFormatter = MUIWebApplication.sharedInstance().delegate.integerFormatter;

    viewDidLoad(){
        super.viewDidLoad();

        // Free registration <-> paid ticket: switching to free clears the price; switching to paid
        // keeps whatever price is typed next
        this.accessSegmented = MUIOutlet(this, 'access-segmented', 'MUISegmentedControl');
        this.accessSegmented.setAction(this, function(this:EventDetailTicketsViewController, control, index:number){
            if (index == 0) {
                this._event.price = 0;
                this.priceTextField.text = this.cf.stringFromNumber(0);
            }
        });

        this.priceTextField = MUIOutlet(this, 'price-tf', 'MUITextField');
        this.priceTextField.formatter = this.cf;
        this.priceTextField.setOnChangeText(this, function(this:EventDetailTicketsViewController, textField:MUITextField, value:string) {
            this._event.price = this.cf.numberFromString(value) || 0;
            this.accessSegmented.selectSegmentedAtIndex(this._event.price > 0 ? 1 : 0);
        });

        this.maxPaxTextField = MUIOutlet(this, 'max-pax-tf', 'MUITextField');
        this.maxPaxTextField.setOnChangeText(this, function(this:EventDetailTicketsViewController, textField:MUITextField, value:string) {
            this._event.maxPax = value.trim().length > 0 ? (parseInt(value) || 0) : null;
            this.updateFigures();
        });

        this.minPaxTextField = MUIOutlet(this, 'min-pax-tf', 'MUITextField');
        this.minPaxTextField.setOnChangeText(this, function(this:EventDetailTicketsViewController, textField:MUITextField, value:string) {
            this._event.minPax = value.trim().length > 0 ? (parseInt(value) || 0) : null;
        });

        this.issuedLabel = MUIOutlet(this, 'issued-lbl', 'MUILabel');
        this.revenueLabel = MUIOutlet(this, 'revenue-lbl', 'MUILabel');
        this.checkinLabel = MUIOutlet(this, 'checkin-lbl', 'MUILabel');
        this.bookingsLabel = MUIOutlet(this, 'bookings-lbl', 'MUILabel');

        this.tableView = MUIOutlet(this, 'table-view', 'UITableView');
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    viewWillAppear(animate?){
        super.viewWillAppear(animate);
        this.updateUI();
    }

    private _event:VenueEvent = null;
    set event(value:VenueEvent){
        this._event = value;
        this.fetchedResultsController = null;
        this.updateUI();
    }

    private updateUI(){
        if (this.viewIsLoaded == false || this._event == null) return;

        this.accessSegmented.selectSegmentedAtIndex((this._event.price || 0) > 0 ? 1 : 0);
        this.priceTextField.text = this.cf.stringFromNumber(this._event.price || 0);
        this.maxPaxTextField.text = this._event.maxPax != null ? String(this._event.maxPax) : "";
        this.minPaxTextField.text = this._event.minPax != null ? String(this._event.minPax) : "";

        this.tableView.reloadData();
        this.updateFigures();
    }

    // Tickets issued (of the capacity), what they add up to, how many were checked in at the door,
    // and the live bookings of the event
    private updateFigures(){
        let tickets = (this.fetchedResultsController?.fetchedObjects ?? []) as EventTicket[];
        let revenue = 0, checked = 0;
        for (let t of tickets) {
            revenue += t.price || 0;
            if (t.consumptionDate != null) checked++;
        }
        let capacity = this._event.maxPax || 0;
        this.issuedLabel.text = this.nf.stringFromNumber(tickets.length) + (capacity > 0 ? " / " + this.nf.stringFromNumber(capacity) : "");
        this.revenueLabel.text = this.cf.stringFromNumber(revenue);
        this.checkinLabel.text = this.nf.stringFromNumber(checked);

        let bookings = 0;
        if (this._event.bookings != null) {
            for (let b of this._event.bookings.allObjects as Booking[]) {
                if (b.deletedAt == null && EventDetailTicketsViewController.liveStatuses.indexOf(b.status) >= 0) bookings += b.pax || 0;
            }
        }
        this.bookingsLabel.text = this.nf.stringFromNumber(bookings) + " " + MIOLocalizeString("PAX", "pax");
    }

    // Booking statuses that still hold places (as the server counts them for the places left)
    private static liveStatuses = [ BookingStatus.BookingRequest, BookingStatus.BookingAssigned, BookingStatus.BookingReserved, BookingStatus.BookingConfirmed, BookingStatus.BookingWaiting, BookingStatus.BookingConfirmedRequest, BookingStatus.BookingSeated, BookingStatus.BookingShow ];

    // --- Table ---

    numberOfSections(tableview){
        return this.fetchedResultsController?.sections.length ?? 0;
    }

    numberOfRowsInSection(tableview, section){
        let sec = this.fetchedResultsController.sections[section];
        return sec.numberOfObjects();
    }

    cellAtIndexPath(tableview:UITableView, indexPath:MIOIndexPath){
        let cell = tableview.dequeueReusableCellWithIdentifier('EventTicketCell') as EventTicketCell;
        cell.item = this.fetchedResultsController.objectAtIndexPath(indexPath) as EventTicket;
        return cell;
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        // Tickets are read-only here: they are issued by the online reservation
    }

    private _fetchedResultsController:MIOFetchedResultsController = null;
    set fetchedResultsController(value){
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;
        this._fetchedResultsController = value;
    }

    get fetchedResultsController(){
        if (this._fetchedResultsController != null) return this._fetchedResultsController;
        if (this._event == null) return null;

        let ad:AppDelegate = MUIWebApplication.sharedInstance().delegate;

        let sortDescriptors = [ MIOSortDescriptor.sortDescriptorWithKey('beginDate', true), MIOSortDescriptor.sortDescriptorWithKey('name', true) ];
        let predicateFormat = "event.identifier == '" + this._event.identifier + "' AND deletedAt == null";

        let fetchRequest = DBHelper.listFetchRequestWithEntityName("EventTicket", sortDescriptors, predicateFormat);
        fetchRequest.relationshipKeyPathsForPrefetching = ["booking"];

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        fetchedResultsController.delegate = this;
        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller){
        this.tableView.reloadData();
        this.updateFigures();
    }
}
